import { useCallback, useEffect, useRef, useState } from "react";

import { getSocket } from "@/lib/socket";
import type { ChatMessage } from "@/features/messaging/types/chat";
import { useAppSelector } from "@/store/hooks";

interface SendAck {
  message?: ChatMessage;
  error?: string;
}

const TYPING_STOP_DELAY_MS = 2000;

/**
 * Every per-conversation real-time concern in one hook, scoped to the
 * conversation page's lifetime: joining/leaving the `chat:<id>` room
 * (`join_chat`/`leave_chat` — docs/notes/sprint1.3.md's contract), sending
 * (with an ack callback for real sent/failed status, not an optimistic
 * guess), receiving (`message_received`, filtered to this chat — the
 * socket is shared app-wide, so an event for a *different* open
 * conversation must be ignored here), typing broadcast/receipt, and
 * marking the latest message seen. `ChatConversationPage` merges
 * `liveMessages` with the paginated history from `useMessages`; this hook
 * never touches that query cache directly.
 */
export function useConversationSocket(chatId: string) {
  const currentUserId = useAppSelector((state) => state.auth.user?.id);
  const [liveMessages, setLiveMessages] = useState<ChatMessage[]>([]);
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const typingStopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);

  useEffect(() => {
    const socket = getSocket();
    if (!socket || !chatId) return;

    socket.emit("join_chat", { chatId });

    function handleMessageReceived(payload: { chatId: string; message: ChatMessage }) {
      if (payload.chatId !== chatId) return;
      setLiveMessages((prev) =>
        prev.some((message) => message.id === payload.message.id)
          ? prev
          : [...prev, payload.message],
      );
    }

    function handleTyping(payload: { chatId: string; userId: string; isTyping: boolean }) {
      if (payload.chatId !== chatId || payload.userId === currentUserId) return;
      setIsOtherTyping(payload.isTyping);
    }

    function handleMessageSeen(payload: { chatId: string; messageId: string; seenBy: string }) {
      if (payload.chatId !== chatId) return;
      setLiveMessages((prev) =>
        prev.map((message) =>
          message.id === payload.messageId && !message.seenBy.includes(payload.seenBy)
            ? { ...message, seenBy: [...message.seenBy, payload.seenBy] }
            : message,
        ),
      );
    }

    socket.on("message_received", handleMessageReceived);
    socket.on("typing", handleTyping);
    socket.on("message_seen", handleMessageSeen);

    return () => {
      socket.emit("leave_chat", { chatId });
      socket.off("message_received", handleMessageReceived);
      socket.off("typing", handleTyping);
      socket.off("message_seen", handleMessageSeen);
      setLiveMessages([]);
      setIsOtherTyping(false);
    };
  }, [chatId, currentUserId]);

  const sendMessage = useCallback(
    (text: string): Promise<ChatMessage> => {
      return new Promise((resolve, reject) => {
        const socket = getSocket();
        if (!socket) {
          reject(new Error("Not connected."));
          return;
        }
        socket.emit("send_message", { chatId, text }, (ack: SendAck) => {
          if (ack.error || !ack.message) {
            reject(new Error(ack.error ?? "Message failed to send."));
            return;
          }
          setLiveMessages((prev) =>
            prev.some((message) => message.id === ack.message!.id) ? prev : [...prev, ack.message!],
          );
          resolve(ack.message);
        });
      });
    },
    [chatId],
  );

  const notifyTyping = useCallback(() => {
    const socket = getSocket();
    if (!socket) return;

    if (!isTypingRef.current) {
      isTypingRef.current = true;
      socket.emit("typing_start", { chatId });
    }

    if (typingStopTimer.current) clearTimeout(typingStopTimer.current);
    typingStopTimer.current = setTimeout(() => {
      isTypingRef.current = false;
      socket.emit("typing_stop", { chatId });
    }, TYPING_STOP_DELAY_MS);
  }, [chatId]);

  const markSeen = useCallback(
    (messageId: string) => {
      const socket = getSocket();
      socket?.emit("mark_seen", { chatId, messageId });
    },
    [chatId],
  );

  return { liveMessages, isOtherTyping, sendMessage, notifyTyping, markSeen };
}
